import React, { useState, useEffect } from 'react';
import { CircleDot, Plus, X, Check, Pencil, Trash2 } from 'lucide-react';
import { sportsApi } from '../../services/sportsApi';
import { Match, MatchStatus, Matchday, Club } from '../../types/sports';

/**
 * Flache Tabellen-Ansicht für Spiele im Datenmanagement
 * Verwendet die gleichen APIs, Controller und Services wie die Matches-Seite
 */
const MatchesDataTable: React.FC = () => {
  const [matches, setMatches] = useState<Match[]>([]);
  const [matchdays, setMatchdays] = useState<Matchday[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<Match>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newMatchData, setNewMatchData] = useState<Partial<Match>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matchesData, matchdaysData, clubsData] = await Promise.all([
        sportsApi.getMatches(),
        sportsApi.getMatchdays(),
        sportsApi.getClubs(),
      ]);
      setMatches(matchesData);
      setMatchdays(matchdaysData);
      setClubs(clubsData);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (match: Match) => {
    setEditingId(match.id);
    setEditData({
      matchdayId: match.matchdayId,
      homeClubId: match.homeClubId,
      awayClubId: match.awayClubId,
      scheduledDate: match.scheduledDate,
      actualDate: match.actualDate,
      homeScore: match.homeScore,
      awayScore: match.awayScore,
      status: match.status,
      notes: match.notes,
      date: match.date,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (id: number) => {
    try {
      await sportsApi.updateMatch(id, editData);
      await loadData();
      setEditingId(null);
      setEditData({});
    } catch (err) {
      console.error('Error saving match:', err);
      setError('Fehler beim Speichern');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await sportsApi.deleteMatch(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting match:', err);
      setError('Fehler beim Löschen');
    }
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    const now = new Date().toISOString();
    setNewMatchData({
      matchdayId: 0,
      homeClubId: 0,
      awayClubId: 0,
      date: now,
      scheduledDate: now,
      status: MatchStatus.Scheduled,
      homeScore: undefined,
      awayScore: undefined,
      notes: '',
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewMatchData({});
  };

  const handleSaveNew = async () => {
    try {
      // Validierung
      if (!newMatchData.matchdayId || newMatchData.matchdayId === 0) {
        setError('Bitte wählen Sie einen Spieltag aus');
        return;
      }
      if (!newMatchData.homeClubId || newMatchData.homeClubId === 0) {
        setError('Bitte wählen Sie einen Heimverein aus');
        return;
      }
      if (!newMatchData.awayClubId || newMatchData.awayClubId === 0) {
        setError('Bitte wählen Sie einen Auswärtsverein aus');
        return;
      }
      if (newMatchData.homeClubId === newMatchData.awayClubId) {
        setError('Heim- und Auswärtsverein müssen unterschiedlich sein');
        return;
      }
      if (!newMatchData.date) {
        setError('Bitte geben Sie ein Spieldatum ein');
        return;
      }
      if (!newMatchData.scheduledDate) {
        setError('Bitte geben Sie ein geplantes Datum ein');
        return;
      }

      await sportsApi.createMatch({
        matchdayId: newMatchData.matchdayId,
        homeClubId: newMatchData.homeClubId,
        awayClubId: newMatchData.awayClubId,
        date: newMatchData.date,
        scheduledDate: newMatchData.scheduledDate,
        actualDate: newMatchData.actualDate,
        status: newMatchData.status ?? MatchStatus.Scheduled,
        homeScore: newMatchData.homeScore,
        awayScore: newMatchData.awayScore,
        notes: newMatchData.notes,
      });

      await loadData();
      setIsCreating(false);
      setNewMatchData({});
      setError(null);
    } catch (err) {
      console.error('Error creating match:', err);
      setError('Fehler beim Erstellen des Spiels');
    }
  };

  const getStatusText = (status: MatchStatus | number | null | undefined) => {
    if (status === null || status === undefined) return 'Unbekannt';
    const statusValue = typeof status === 'number' ? status : Number(status);
    switch (statusValue) {
      case MatchStatus.Scheduled: return 'Geplant';
      case MatchStatus.Live: return 'Live';
      case MatchStatus.Finished: return 'Beendet';
      case MatchStatus.Postponed: return 'Verschoben';
      case MatchStatus.Cancelled: return 'Abgesagt';
      default: return 'Unbekannt';
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const parseDateTime = (dateString: string) => {
    return new Date(dateString).toISOString();
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Lade Spiele...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>{error}</div>
        <button onClick={loadData} style={buttonStyle}>Erneut versuchen</button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}><CircleDot size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Spiele Datenmanagement</h2>
        <button onClick={handleStartCreate} style={createButtonStyle} disabled={isCreating}>
          <Plus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Neues Spiel
        </button>
      </div>

      {error && (
        <div style={errorBannerStyle}>
          {error}
          <button onClick={() => setError(null)} style={errorCloseButtonStyle}><X size={14} /></button>
        </div>
      )}

      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr style={headerRowStyle}>
              <th style={headerCellStyle}>ID</th>
              <th style={headerCellStyle}>Spieltag</th>
              <th style={headerCellStyle}>Heimverein</th>
              <th style={headerCellStyle}>Auswärtsverein</th>
              <th style={headerCellStyle}>Ergebnis</th>
              <th style={headerCellStyle}>Spieldatum</th>
              <th style={headerCellStyle}>Geplant</th>
              <th style={headerCellStyle}>Tatsächlich</th>
              <th style={headerCellStyle}>Status</th>
              <th style={headerCellStyle}>Notizen</th>
              <th style={headerCellStyle}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {/* Neue Zeile für das Erstellen eines Spiels */}
            {isCreating && (
              <tr style={{...rowStyle, backgroundColor: '#f0f9ff'}}>
                <td style={cellStyle}>
                  <span style={newBadgeStyle}>NEU</span>
                </td>

                {/* Spieltag */}
                <td style={cellStyle}>
                  <select
                    value={newMatchData.matchdayId || 0}
                    onChange={(e) => setNewMatchData({ ...newMatchData, matchdayId: Number(e.target.value) })}
                    style={selectStyle}
                  >
                    <option value={0}>Auswählen...</option>
                    {matchdays.map(md => (
                      <option key={md.id} value={md.id}>
                        {md.name} ({md.season?.name})
                      </option>
                    ))}
                  </select>
                </td>

                {/* Heimverein */}
                <td style={cellStyle}>
                  <select
                    value={newMatchData.homeClubId || 0}
                    onChange={(e) => setNewMatchData({ ...newMatchData, homeClubId: Number(e.target.value) })}
                    style={selectStyle}
                  >
                    <option value={0}>Auswählen...</option>
                    {clubs.map(club => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Auswärtsverein */}
                <td style={cellStyle}>
                  <select
                    value={newMatchData.awayClubId || 0}
                    onChange={(e) => setNewMatchData({ ...newMatchData, awayClubId: Number(e.target.value) })}
                    style={selectStyle}
                  >
                    <option value={0}>Auswählen...</option>
                    {clubs.map(club => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Ergebnis */}
                <td style={cellStyle}>
                  <div style={scoreContainerStyle}>
                    <input
                      type="number"
                      min="0"
                      value={newMatchData.homeScore ?? ''}
                      onChange={(e) => setNewMatchData({ ...newMatchData, homeScore: e.target.value ? Number(e.target.value) : undefined })}
                      style={scoreInputStyle}
                      placeholder="H"
                    />
                    :
                    <input
                      type="number"
                      min="0"
                      value={newMatchData.awayScore ?? ''}
                      onChange={(e) => setNewMatchData({ ...newMatchData, awayScore: e.target.value ? Number(e.target.value) : undefined })}
                      style={scoreInputStyle}
                      placeholder="A"
                    />
                  </div>
                </td>

                {/* Spieldatum */}
                <td style={cellStyle}>
                  <input
                    type="datetime-local"
                    value={formatDateTime(newMatchData.date)}
                    onChange={(e) => setNewMatchData({ ...newMatchData, date: parseDateTime(e.target.value) })}
                    style={inputStyle}
                  />
                </td>

                {/* Geplantes Datum */}
                <td style={cellStyle}>
                  <input
                    type="datetime-local"
                    value={formatDateTime(newMatchData.scheduledDate)}
                    onChange={(e) => setNewMatchData({ ...newMatchData, scheduledDate: parseDateTime(e.target.value) })}
                    style={inputStyle}
                  />
                </td>

                {/* Tatsächliches Datum */}
                <td style={cellStyle}>
                  <input
                    type="datetime-local"
                    value={formatDateTime(newMatchData.actualDate)}
                    onChange={(e) => setNewMatchData({ ...newMatchData, actualDate: e.target.value ? parseDateTime(e.target.value) : undefined })}
                    style={inputStyle}
                  />
                </td>

                {/* Status */}
                <td style={cellStyle}>
                  <select
                    value={newMatchData.status ?? MatchStatus.Scheduled}
                    onChange={(e) => setNewMatchData({ ...newMatchData, status: Number(e.target.value) })}
                    style={selectStyle}
                  >
                    <option value={MatchStatus.Scheduled}>Geplant</option>
                    <option value={MatchStatus.Live}>Live</option>
                    <option value={MatchStatus.Finished}>Beendet</option>
                    <option value={MatchStatus.Postponed}>Verschoben</option>
                    <option value={MatchStatus.Cancelled}>Abgesagt</option>
                  </select>
                </td>

                {/* Notizen */}
                <td style={cellStyle}>
                  <input
                    type="text"
                    value={newMatchData.notes || ''}
                    onChange={(e) => setNewMatchData({ ...newMatchData, notes: e.target.value })}
                    style={inputStyle}
                    placeholder="Notizen..."
                  />
                </td>

                {/* Aktionen */}
                <td style={cellStyle}>
                  <div style={actionsStyle}>
                    <button onClick={handleSaveNew} style={saveButtonStyle} title="Speichern">
                      <Check size={14} />
                    </button>
                    <button onClick={handleCancelCreate} style={cancelButtonStyle} title="Abbrechen">
                      <X size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* Bestehende Spiele */}
            {matches.map((match) => (
              <tr key={match.id} style={rowStyle}>
                {editingId === match.id ? (
                  <>
                    {/* ID (nicht editierbar) */}
                    <td style={cellStyle}>{match.id}</td>

                    {/* Spieltag */}
                    <td style={cellStyle}>
                      <select
                        value={editData.matchdayId || 0}
                        onChange={(e) => setEditData({ ...editData, matchdayId: Number(e.target.value) })}
                        style={selectStyle}
                      >
                        <option value={0}>Auswählen...</option>
                        {matchdays.map(md => (
                          <option key={md.id} value={md.id}>
                            {md.name} ({md.season?.name})
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Heimverein */}
                    <td style={cellStyle}>
                      <select
                        value={editData.homeClubId || 0}
                        onChange={(e) => setEditData({ ...editData, homeClubId: Number(e.target.value) })}
                        style={selectStyle}
                      >
                        <option value={0}>Auswählen...</option>
                        {clubs.map(club => (
                          <option key={club.id} value={club.id}>
                            {club.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Auswärtsverein */}
                    <td style={cellStyle}>
                      <select
                        value={editData.awayClubId || 0}
                        onChange={(e) => setEditData({ ...editData, awayClubId: Number(e.target.value) })}
                        style={selectStyle}
                      >
                        <option value={0}>Auswählen...</option>
                        {clubs.map(club => (
                          <option key={club.id} value={club.id}>
                            {club.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Ergebnis */}
                    <td style={cellStyle}>
                      <div style={scoreContainerStyle}>
                        <input
                          type="number"
                          min="0"
                          value={editData.homeScore ?? ''}
                          onChange={(e) => setEditData({ ...editData, homeScore: e.target.value ? Number(e.target.value) : undefined })}
                          style={scoreInputStyle}
                          placeholder="H"
                        />
                        :
                        <input
                          type="number"
                          min="0"
                          value={editData.awayScore ?? ''}
                          onChange={(e) => setEditData({ ...editData, awayScore: e.target.value ? Number(e.target.value) : undefined })}
                          style={scoreInputStyle}
                          placeholder="A"
                        />
                      </div>
                    </td>

                    {/* Spieldatum */}
                    <td style={cellStyle}>
                      <input
                        type="datetime-local"
                        value={formatDateTime(editData.date)}
                        onChange={(e) => setEditData({ ...editData, date: parseDateTime(e.target.value) })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Geplantes Datum */}
                    <td style={cellStyle}>
                      <input
                        type="datetime-local"
                        value={formatDateTime(editData.scheduledDate)}
                        onChange={(e) => setEditData({ ...editData, scheduledDate: parseDateTime(e.target.value) })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Tatsächliches Datum */}
                    <td style={cellStyle}>
                      <input
                        type="datetime-local"
                        value={formatDateTime(editData.actualDate)}
                        onChange={(e) => setEditData({ ...editData, actualDate: e.target.value ? parseDateTime(e.target.value) : undefined })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Status */}
                    <td style={cellStyle}>
                      <select
                        value={editData.status ?? MatchStatus.Scheduled}
                        onChange={(e) => setEditData({ ...editData, status: Number(e.target.value) })}
                        style={selectStyle}
                      >
                        <option value={MatchStatus.Scheduled}>Geplant</option>
                        <option value={MatchStatus.Live}>Live</option>
                        <option value={MatchStatus.Finished}>Beendet</option>
                        <option value={MatchStatus.Postponed}>Verschoben</option>
                        <option value={MatchStatus.Cancelled}>Abgesagt</option>
                      </select>
                    </td>

                    {/* Notizen */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        value={editData.notes || ''}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        style={inputStyle}
                        placeholder="Notizen..."
                      />
                    </td>

                    {/* Aktionen */}
                    <td style={cellStyle}>
                      <div style={actionsStyle}>
                        <button onClick={() => handleSave(match.id)} style={saveButtonStyle} title="Speichern">
                          <Check size={14} />
                        </button>
                        <button onClick={handleCancelEdit} style={cancelButtonStyle} title="Abbrechen">
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    {/* Ansichtsmodus */}
                    <td style={cellStyle}>{match.id}</td>
                    <td style={cellStyle}>
                      {match.matchday?.name || `ID: ${match.matchdayId}`}
                      <br />
                      <small style={smallTextStyle}>{match.matchday?.season?.name}</small>
                    </td>
                    <td style={cellStyle}>{match.homeClub?.name || `ID: ${match.homeClubId}`}</td>
                    <td style={cellStyle}>{match.awayClub?.name || `ID: ${match.awayClubId}`}</td>
                    <td style={cellStyle}>
                      {(match.homeScore !== null && match.homeScore !== undefined &&
                        match.awayScore !== null && match.awayScore !== undefined)
                        ? `${match.homeScore} : ${match.awayScore}`
                        : '-'}
                    </td>
                    <td style={cellStyle}>
                      {match.date ? new Date(match.date).toLocaleString('de-DE') : '-'}
                    </td>
                    <td style={cellStyle}>
                      {new Date(match.scheduledDate).toLocaleString('de-DE')}
                    </td>
                    <td style={cellStyle}>
                      {match.actualDate ? new Date(match.actualDate).toLocaleString('de-DE') : '-'}
                    </td>
                    <td style={cellStyle}>
                      <span style={statusBadgeStyle}>{getStatusText(match.status)}</span>
                    </td>
                    <td style={cellStyle}>
                      <div style={notesCellStyle}>{match.notes || '-'}</div>
                    </td>
                    <td style={cellStyle}>
                      <div style={actionsStyle}>
                        <button onClick={() => handleEdit(match)} style={editButtonStyle} title="Bearbeiten">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setDeleteConfirm(match.id)} style={deleteButtonStyle} title="Löschen">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={modalTitleStyle}>Spiel löschen</h3>
            <p style={modalTextStyle}>
              Sind Sie sicher, dass Sie dieses Spiel löschen möchten?
            </p>
            <div style={modalActionsStyle}>
              <button onClick={() => setDeleteConfirm(null)} style={modalCancelButtonStyle}>
                Abbrechen
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} style={modalDeleteButtonStyle}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  padding: '1rem',
  width: '100%',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.5rem',
  color: '#2c3e50',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '2rem',
  color: '#7f8c8d',
};

const errorStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '1rem',
  color: '#e74c3c',
  marginBottom: '1rem',
};

const buttonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: '#3498db',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

const createButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: '#27ae60',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
};

const tableContainerStyle: React.CSSProperties = {
  overflowX: 'auto',
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const headerRowStyle: React.CSSProperties = {
  background: '#f8f9fa',
  borderBottom: '2px solid #dee2e6',
};

const headerCellStyle: React.CSSProperties = {
  padding: '0.75rem 0.5rem',
  textAlign: 'left',
  fontWeight: 'bold',
  color: '#2c3e50',
  whiteSpace: 'nowrap',
};

const rowStyle: React.CSSProperties = {
  borderBottom: '1px solid #e9ecef',
};

const cellStyle: React.CSSProperties = {
  padding: '0.5rem',
  verticalAlign: 'middle',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.25rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '0.85rem',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.25rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '0.85rem',
};

const scoreContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
};

const scoreInputStyle: React.CSSProperties = {
  width: '40px',
  padding: '0.25rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '0.85rem',
  textAlign: 'center',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.25rem',
  justifyContent: 'center',
};

const editButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
};

const deleteButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1rem',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: '#dc3545',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
};

const statusBadgeStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderRadius: '12px',
  fontSize: '0.75rem',
  background: '#e9ecef',
  color: '#495057',
  whiteSpace: 'nowrap',
};

const smallTextStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#6c757d',
};

const notesCellStyle: React.CSSProperties = {
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '1.5rem',
  maxWidth: '400px',
  width: '90%',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

const modalTitleStyle: React.CSSProperties = {
  margin: '0 0 1rem 0',
  fontSize: '1.25rem',
  color: '#2c3e50',
};

const modalTextStyle: React.CSSProperties = {
  margin: '0 0 1.5rem 0',
  color: '#6c757d',
};

const modalActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.5rem',
};

const modalCancelButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  background: 'white',
  color: '#495057',
  cursor: 'pointer',
};

const modalDeleteButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: 'none',
  borderRadius: '4px',
  background: '#dc3545',
  color: 'white',
  cursor: 'pointer',
};

const newBadgeStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderRadius: '12px',
  fontSize: '0.7rem',
  background: '#10b981',
  color: 'white',
  fontWeight: 'bold',
};

const errorBannerStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  background: '#fee2e2',
  border: '1px solid #fecaca',
  borderRadius: '6px',
  color: '#dc2626',
  marginBottom: '1rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const errorCloseButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#dc2626',
  cursor: 'pointer',
  fontSize: '1.2rem',
  padding: '0 0.5rem',
};

export default MatchesDataTable;

